import React, { ReactElement, RefObject, useRef } from "react";
import { Extent } from "ol/extent";
import { Map, useMapContext } from "@avinet/adaptive-ui-maps";
import Zoom from "@avinet/adaptive-ui-maps/control/Zoom";
import ZoomToExtent from "@avinet/adaptive-ui-maps/control/ZoomToExtent";
import useCachedExtent from "../../hooks/useCachedExtent";
import useResizeElement from "../../hooks/useResizeElement";
import { getAuth } from "../../utils/baat";
import Layers from "./Layers";
import MapListener from "./MapListener";
import "./DefaultMap.scss";

const defaultLayers = [
  {
    sourceType: "wmts",
    id: "topograatone",
    name: "Norgeskart",
    uri: "https://cache.kartverket.no/v1/wmts/1.0.0/topograatone",
    layers: "topograatone",
    version: "1.0.0",
    attributions: "&copy; Kartverket",
  },
];

const defaultExtent: Extent = [500000, 7400000, 3600000, 12000000];

interface DefaultMapProps {
  children?: React.ReactNode;
  layers?: any[];
  initialExtent?: number[];
}

interface MapResizerProps<T extends HTMLElement> {
  monitorRef: RefObject<T>;
}

function MapResizer<T extends HTMLElement>({
  monitorRef,
}: MapResizerProps<T>): ReactElement | null {
  const { forceUpdateMap } = useMapContext();
  useResizeElement(monitorRef, forceUpdateMap);
  return null;
}

const DefaultMap = ({ children, layers, initialExtent }: DefaultMapProps) => {
  const divRef = useRef<HTMLDivElement>(null);

  const [cachedExtent, setCachedExtent] = useCachedExtent();

  const extent = cachedExtent ?? initialExtent ?? defaultExtent;

  return (
    <div ref={divRef} className="default-map">
      <Map minZoom={4} maxZoom={18} initialExtent={extent} auth={getAuth()}>
        <Zoom />
        <MapResizer monitorRef={divRef} />
        <MapListener onExtentChange={setCachedExtent} />
        <Layers layers={layers ?? defaultLayers} />
        <ZoomToExtent extent={initialExtent ?? defaultExtent} />
        {children}
      </Map>
    </div>
  );
};

export default DefaultMap;
